const DEFAULT_IMAGE =
  'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTq6sYyW3nIRd6e2ghFLSPdyzFt2AqMC6Go_A&s';

const COMMON_SERVICE_CATEGORIES = new Set([
  'Aadhaar Services',
  'PAN Card Services',
  'Passport Services',
  'Voter ID Services',
  'Driving Licence',
  'Birth / Death Certificate',
  'Caste / Domicile Certificate',
  'Income Certificate',
  'Residence Certificate',
  'Ration Card Services',
  'Jan Dhan & Banking Services',
  'Digital Payments & UPI',
]);

const toAppItem = (svc) => ({
  applicationName:        svc.serviceName        || '',
  applicationStartDate:   svc.startDate          || '',
  applicationImageURL:    svc.serviceImagePreview || DEFAULT_IMAGE,
  applicationDescription: svc.serviceDescription || '',
  applicationLastDate:    svc.endDate            || '',
  serviceState:           svc.state              || '',
});

const toList = (map) =>
  Object.entries(map).map(([name, list]) => ({
    applicationsCategoryName: name,
    applicationsListByCategory: list,
  }));

// Build a deduplication key for a service.
// Rule 1: same officialLink (non-empty) → definitely the same notification.
// Rule 2: same serviceCategory + state + startDate → same exam cycle even if
//         different CSC owners typed different names for it.
export const getServiceKey = (svc) => {
  // Best key: catalog sub-category name (canonical, owner-picked from dropdown)
  const sub = (svc.subCategory || '').trim().toLowerCase();
  if (sub) return `sub::${sub}`;
  // Fallback: category + state + start date (handles old/pre-catalog services)
  const cat   = (svc.serviceCategory || '').toLowerCase();
  const state = (svc.state          || '').toLowerCase();
  const start = (svc.startDate      || '').toLowerCase();
  return `cat::${cat}|state::${state}|start::${start}`;
};

// When multiple CSC owners register the same service, show it once on the
// home page. The full provider list is resolved at service-detail time from
// the unfiltered localStorage array.
const deduplicateServices = (services) => {
  const seen = new Set();
  return services.filter((svc) => {
    const key = getServiceKey(svc);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const formatServicesToApplications = (services) => {
  const unique = deduplicateServices(services);

  const byQualification = {};
  const byState        = {};
  const byCategory     = {};
  const commonList     = [];

  unique.forEach((svc) => {
    const item = toAppItem(svc);

    (svc.qualifications || []).forEach((q) => {
      if (q && q !== 'NA') {
        if (!byQualification[q]) byQualification[q] = [];
        byQualification[q].push(item);
      }
    });

    if (svc.state) {
      if (!byState[svc.state]) byState[svc.state] = [];
      byState[svc.state].push(item);
    }

    if (svc.serviceCategory) {
      if (!byCategory[svc.serviceCategory]) byCategory[svc.serviceCategory] = [];
      byCategory[svc.serviceCategory].push(item);
    }

    if (COMMON_SERVICE_CATEGORIES.has(svc.serviceCategory)) {
      commonList.push(item);
    }
  });

  // Merge All India services into every state bucket so that when a user
  // selects a specific state they also see universally available services.
  const allIndiaItems = byState['All India'] || [];
  if (allIndiaItems.length) {
    delete byState['All India'];
    Object.keys(byState).forEach((state) => {
      byState[state] = [...allIndiaItems, ...byState[state]];
    });
  }

  return [
    {
      applicationCategory: 'qualification',
      applicationsList: toList(byQualification),
    },
    {
      applicationCategory: 'state',
      applicationsList: toList(byState),
    },
    {
      applicationCategory: 'popular-category',
      applicationsList: toList(byCategory),
    },
    {
      applicationCategory: 'common-services',
      applicationsList: commonList.length
        ? [{ applicationsCategoryName: 'Common Services', applicationsListByCategory: commonList }]
        : [],
    },
  ];
};

export const getServicesFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('cscServices') || '[]');
  } catch {
    return [];
  }
};
